import { store } from '@redux/store';
import { filter, fromEvent, map, merge, switchMap, of, EMPTY } from 'rxjs';
import { googleAnalytics4Event } from 'features/googleAnalytics4';
import typeahead from "features/articleSearchBox";
import { selectPage } from 'features/articleSearchResultsFacets';

// Helper functions to retrieve DOM elements related to events
const getPopularPillElement = (event) => event.target.closest('[data-pill]');
const getArticleElement = (event) => event.target.closest('[data-article-card-link]');
const isArticleCard = (event) => !!event.target.closest('[data-article-card-link]');
const getLinkableElement = (event) => event.target.closest('[data-page-link]');

// DOM elements for specific sections
const paginationSectionElement = document.querySelector('[data-component="article-search-results-pagination"]');
const articlesSectionElement = document.querySelector('[data-component="article-search-results"]');
const popularPillsSectionElement = document.querySelector('[data-section-name="popularPills"]');
const featuredArticleSectionElement = document.querySelector('[data-component="featured-article"]');
const previousButton = paginationSectionElement.querySelector('[data-prev-button]');
const nextButton = paginationSectionElement.querySelector('[data-next-button]');
const optionList = paginationSectionElement.querySelector('[data-option-list]');

/**
 * @function triggerPopularPillClick
 * @description Handles the click event on a popular tag and dispatches a tracking event to Google Analytics 4.
 * @param {HTMLElement} pillElement - The HTML element of the popular tag.
 */
const triggerPopularPillClick = function(pillElement) {
  const customProperties = {
    page_type: 'News_Trends',
    tag_text: pillElement.dataset.pillText,
    destination_url: pillElement.dataset.pillUrl,
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'tag_click', customProperties }));
};

// Observable to handle clicks on popular tags
const onPopularPillClick$ = fromEvent(popularPillsSectionElement, 'click').pipe(
  map(getPopularPillElement),
  filter(Boolean)
);
onPopularPillClick$.subscribe(triggerPopularPillClick);

/**
 * @function triggerFeaturedArticleClick
 * @description Handles the click event on a featured article and dispatches a tracking event to Google Analytics 4.
 * @param {Object} params - Event parameters.
 * @param {HTMLElement} params.featuredArticleCard - The HTML element of the featured article.
 * @param {string} params.interactionType - The type of interaction (e.g., "Article Clicked").
 */
const triggerFeaturedArticleClick = function({ featuredArticleCard, interactionType }) {
  const customProperties = {
    article_id: featuredArticleCard.dataset.slug,
    article_title: featuredArticleCard.dataset.title,
    page_type: 'News_Trends',
    click_target: interactionType,
    destination: featuredArticleCard.dataset.url,
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'featured_article_click', customProperties }));
};

// Observable to handle clicks on featured articles
const onFeaturedArticleClick$ = featuredArticleSectionElement
  ? fromEvent(featuredArticleSectionElement, 'click').pipe(
    filter(isArticleCard),
    map(event => ({
      featuredArticleCard: getArticleElement(event),
      interactionType: event.target.dataset.interactionType || 'Article Clicked'
    })),
  ) : EMPTY;
onFeaturedArticleClick$.subscribe(triggerFeaturedArticleClick);

/**
 * @function triggerPageViewEvent
 * @description Dispatches a page view event to Google Analytics 4 when the page loads.
 */
const triggerPageViewEvent = function() {
  const customProperties = {
    page_type: 'News_Trends',
    page_title: 'News And Trends',
    page_url: window.location.href,
    content_type: 'landing',
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'page_view', customProperties }));
};

// Observable to handle the window load event
const onWindowLoad$ = fromEvent(window, 'load');
onWindowLoad$.subscribe(triggerPageViewEvent);

/**
 * @function triggerSearchExecutedEvent
 * @description Handles the search execution event and dispatches a tracking event to Google Analytics 4.
 * @param {HTMLElement} suggestionElement - The HTML element of the selected suggestion.
 */
const triggerSearchExecutedEvent = function(suggestionElement) {
  const customProperties = {
    page_type: 'News_Trends',
    search_query: suggestionElement.dataset.title,
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'searchterm_events', customProperties }));
};

// Observable to handle clicks on search suggestions
typeahead.getSuggestionListClickedObserver().subscribe(triggerSearchExecutedEvent);

/**
 * @function triggerArticleClick
 * @description Handles the click event on an article and dispatches a tracking event to Google Analytics 4.
 * @param {Object} params - Event parameters.
 * @param {HTMLElement} params.articleCard - The HTML element of the article.
 * @param {string} params.interactionType - The type of interaction (e.g., "Article Clicked").
 */
const triggerArticleClick = function({ articleCard, interactionType }) {
  const customProperties = {
    article_id: articleCard.dataset.slug,
    article_title: articleCard.dataset.title,
    page_type: 'News_Trends',
    click_target: interactionType,
    destination: articleCard.dataset.url,
    position: articleCard.dataset.index,
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'article_card_click', customProperties }));
};

// Observable to handle clicks on popular articles
const onMostPopularArticlesClick$ = fromEvent(articlesSectionElement, 'click').pipe(
  filter(isArticleCard),
  map(event => ({
    articleCard: getArticleElement(event),
    interactionType: event.target.dataset.interactionType || 'Article Clicked'
  })),
);
onMostPopularArticlesClick$.subscribe(triggerArticleClick);

/**
 * @function triggerPaginationClickEvent
 * @description Handles the pagination click event and dispatches a tracking event to Google Analytics 4.
 * @param {Object} customProperties - Custom properties of the event.
 */
const triggerPaginationClickEvent = function(customProperties) {
  store.dispatch(googleAnalytics4Event({ eventName: 'pagination_click', customProperties }));
};

// Observables to handle clicks on pagination buttons and page options
const previousButton$ = fromEvent(previousButton, 'click').pipe(
  map(() => selectPage(store.getState())),
  map((page) => ({
    current_page: page - 1,
    destination_page: page,
    navigation_method: 'previous',
    page_type: 'News_Trends'
  })),
);

const nextButton$ = fromEvent(nextButton, 'click').pipe(
  map(() => selectPage(store.getState())),
  map((page) => ({
    current_page: page + 1,
    destination_page: page,
    navigation_method: 'next',
    page_type: 'News_Trends'
  })),
);

const optionList$ = fromEvent(optionList, 'click').pipe(
  switchMap((event) => {
    const linkableElement = getLinkableElement(event);
    if (linkableElement) {
      event.preventDefault();
    }
    const previousPage = selectPage(store.getState());
    const page = Number(linkableElement?.dataset?.pageLink || 0);
    return of({
      current_page: page,
      destination_page: previousPage,
      navigation_method: 'page_number',
      page_type: 'News_Trends'
    });
  })
);

// Merge all pagination-related observables
merge(previousButton$, nextButton$, optionList$).subscribe(triggerPaginationClickEvent);
